import type {
  Admission,
  AdminGuestPreview,
  AgentRecord,
  ApiEnvelope,
  ApiMeta,
  AttendanceResponse,
  AuditEvent,
  Ceremony,
  CeremonyTable,
  CheckinCeremony,
  CheckinGuestMatch,
  CheckinStats,
  CheckinVerification,
  DashboardData,
  DressCode,
  FieldError,
  GuestInvitation,
  GuestPortalData,
  GuestRecord,
  MediaRecord,
  PageData,
  PublicWedding,
  SessionData,
  WeddingAdmin,
} from './types';

const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') || '/api/v1';
const CSRF_STORAGE_KEY = 'orry-rudy.csrf';

interface ApiErrorPayload {
  error?: {
    code?: string;
    message?: string;
    fieldErrors?: FieldError[];
  };
  meta?: ApiMeta;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: FieldError[];
  readonly requestId?: string;

  constructor(status: number, payload?: ApiErrorPayload) {
    super(payload?.error?.message || defaultMessage(status));
    this.name = 'ApiError';
    this.status = status;
    this.code = payload?.error?.code || `HTTP_${status}`;
    this.fieldErrors = payload?.error?.fieldErrors || [];
    this.requestId = payload?.meta?.requestId;
  }
}

function defaultMessage(status: number) {
  if (status === 401) return 'Votre session a expiré. Veuillez vous reconnecter.';
  if (status === 403) return "Vous n'avez pas l'autorisation d'effectuer cette action.";
  if (status === 404) return 'Cette ressource est indisponible.';
  if (status === 409) return 'Les données ont changé. Rechargez puis réessayez.';
  if (status === 429) return 'Trop de tentatives. Patientez avant de réessayer.';
  if (status >= 500) return 'Le service est momentanément indisponible.';
  return "Une erreur empêche de terminer l'action.";
}

function isBodyInit(value: unknown): value is BodyInit {
  return (
    typeof value === 'string' ||
    value instanceof Blob ||
    value instanceof FormData ||
    value instanceof URLSearchParams ||
    value instanceof ArrayBuffer
  );
}

export function setCsrfToken(token?: string) {
  if (token) sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  else sessionStorage.removeItem(CSRF_STORAGE_KEY);
}

export function getCsrfToken() {
  return sessionStorage.getItem(CSRF_STORAGE_KEY) || undefined;
}

async function parseError(response: Response): Promise<ApiErrorPayload | undefined> {
  try {
    return (await response.json()) as ApiErrorPayload;
  } catch {
    return undefined;
  }
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  const method = (init.method || 'GET').toUpperCase();
  const csrf = getCsrfToken();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrf) headers.set('X-CSRF-Token', csrf);

  let body = init.body;
  if (body && !isBodyInit(body)) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers,
      body,
      credentials: 'include',
    });
  } catch {
    throw new ApiError(0, {
      error: {
        code: 'NETWORK_UNAVAILABLE',
        message: 'Connexion au serveur impossible. Vérifiez votre réseau.',
      },
    });
  }

  if (!response.ok) throw new ApiError(response.status, await parseError(response));
  if (response.status === 204) return undefined as T;

  const payload = (await response.json()) as ApiEnvelope<T> | T;
  return typeof payload === 'object' && payload !== null && 'data' in payload
    ? (payload as ApiEnvelope<T>).data
    : (payload as T);
}

async function apiBlob(path: string): Promise<{ blob: Blob; filename?: string }> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: { Accept: 'application/pdf' },
    });
  } catch {
    throw new ApiError(0, {
      error: { code: 'NETWORK_UNAVAILABLE', message: 'Téléchargement impossible sans connexion.' },
    });
  }
  if (!response.ok) throw new ApiError(response.status, await parseError(response));
  const disposition = response.headers.get('content-disposition') || '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quoted = disposition.match(/filename="([^"]+)"/i)?.[1];
  return {
    blob: await response.blob(),
    filename: encoded ? decodeURIComponent(encoded) : quoted,
  };
}

function query(params: Record<string, string | number | undefined | null>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const value = search.toString();
  return value ? `?${value}` : '';
}

export function unwrapItems<T>(value: PageData<T> | T[] | undefined): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value.items || value.data || value.results || [];
}

export const api = {
  publicWedding: () => apiRequest<PublicWedding>('/public/wedding'),
  session: () => apiRequest<SessionData>('/auth/session'),
  logout: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
  requestGuestCode: (countryCode: string, phone: string) =>
    apiRequest<{ challengeId: string; expiresIn: number; resendAfter: number; message?: string }>(
      '/auth/guest/challenges',
      { method: 'POST', body: { countryCode, phone } as never },
    ),
  verifyGuestCode: (challengeId: string, code: string) =>
    apiRequest<SessionData>(`/auth/guest/challenges/${encodeURIComponent(challengeId)}/verify`, {
      method: 'POST',
      body: { code } as never,
    }),
  loginGuestByPhone: (countryCode: string, phone: string) =>
    apiRequest<SessionData>('/auth/guest/login', {
      method: 'POST',
      body: { countryCode, phone } as never,
    }),
  staffLogin: (email: string, password: string) =>
    apiRequest<SessionData>('/auth/staff/login', {
      method: 'POST',
      body: { email, password } as never,
    }),
  staffRecovery: (email: string) =>
    apiRequest<{ message?: string }>('/auth/staff/recovery', {
      method: 'POST',
      body: { email } as never,
    }),
  guestPortal: () =>
    apiRequest<GuestPortalData>(`/guest/me/invitations?refresh=${Date.now()}`, {
      cache: 'no-store',
    }),
  guestInvitation: (id: string) =>
    apiRequest<GuestInvitation>(`/guest/me/invitations/${encodeURIComponent(id)}`),
  setAttendance: (assignmentId: string, response: AttendanceResponse) =>
    apiRequest<GuestInvitation>(
      `/guest/me/invitations/${encodeURIComponent(assignmentId)}/attendance`,
      { method: 'POST', body: { response } as never },
    ),
  invitationPdf: (id: string) => apiBlob(`/guest/me/invitations/${encodeURIComponent(id)}/pdf`),
  invitationFile: () => apiBlob('/guest/me/invitation-file'),

  dashboard: (ceremonyId?: string) =>
    apiRequest<DashboardData>(`/admin/dashboard${query({ ceremonyId })}`),
  wedding: () => apiRequest<WeddingAdmin>('/admin/wedding'),
  updateWedding: (payload: Record<string, unknown>) =>
    apiRequest<WeddingAdmin>('/admin/wedding', { method: 'PATCH', body: payload as never }),
  publishWedding: (version: number) =>
    apiRequest<WeddingAdmin>('/admin/wedding/publish', {
      method: 'POST',
      body: { version } as never,
    }),
  weddingAction: (action: 'suspend' | 'reactivate' | 'archive', reason: string) =>
    apiRequest<WeddingAdmin>(`/admin/wedding/${action}`, {
      method: 'POST',
      body: { reason } as never,
    }),
  uploadMedia: (file: File, altText: string, ceremonyId?: string) => {
    const form = new FormData();
    form.set('file', file);
    form.set('altText', altText);
    if (ceremonyId) form.set('ceremonyId', ceremonyId);
    return apiRequest<MediaRecord>('/admin/media', { method: 'POST', body: form });
  },
  updateMedia: (id: string, payload: Record<string, unknown>) =>
    apiRequest<MediaRecord>(`/admin/media/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload as never,
    }),
  retireMedia: (id: string, reason: string) =>
    apiRequest<MediaRecord>(`/admin/media/${encodeURIComponent(id)}/retire`, {
      method: 'POST',
      body: { reason } as never,
    }),

  ceremonies: () => apiRequest<PageData<Ceremony> | Ceremony[]>('/admin/ceremonies'),
  createCeremony: (payload: Record<string, unknown>) =>
    apiRequest<Ceremony>('/admin/ceremonies', { method: 'POST', body: payload as never }),
  updateCeremony: (id: string, payload: Record<string, unknown>) =>
    apiRequest<Ceremony>(`/admin/ceremonies/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload as never,
    }),
  publishCeremony: (id: string, version: number) =>
    apiRequest<Ceremony>(`/admin/ceremonies/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: { version } as never,
    }),
  ceremonyAction: (id: string, action: 'suspend' | 'reactivate' | 'archive', reason: string) =>
    apiRequest<Ceremony>(`/admin/ceremonies/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      body: { reason } as never,
    }),
  deleteCeremony: (id: string, reason: string) =>
    apiRequest<void>(`/admin/ceremonies/${encodeURIComponent(id)}/delete`, {
      method: 'POST',
      body: { reason } as never,
    }),
  assignmentAction: (id: string, action: 'suspend' | 'reactivate' | 'revoke', reason: string) =>
    apiRequest(`/admin/assignments/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      body: { reason } as never,
    }),

  ceremonyTables: (ceremonyId: string) =>
    apiRequest<PageData<CeremonyTable> | CeremonyTable[]>(
      `/admin/ceremonies/${encodeURIComponent(ceremonyId)}/tables`,
    ),
  ceremonyTablesPdf: (ceremonyId: string) =>
    apiBlob(`/admin/ceremonies/${encodeURIComponent(ceremonyId)}/tables/pdf`),
  createTable: (ceremonyId: string, payload: Record<string, unknown>) =>
    apiRequest<CeremonyTable>(`/admin/ceremonies/${encodeURIComponent(ceremonyId)}/tables`, {
      method: 'POST',
      body: payload as never,
    }),
  updateTable: (tableId: string, payload: Record<string, unknown>) =>
    apiRequest<CeremonyTable>(`/admin/tables/${encodeURIComponent(tableId)}`, {
      method: 'PATCH',
      body: payload as never,
    }),
  moveTable: (tableId: string, x: number, y: number) =>
    apiRequest<CeremonyTable>(`/admin/tables/${encodeURIComponent(tableId)}/position`, {
      method: 'PATCH',
      body: { x, y } as never,
    }),
  deleteTable: (tableId: string) =>
    apiRequest<void>(`/admin/tables/${encodeURIComponent(tableId)}`, { method: 'DELETE' }),
  seatGuest: (tableId: string, guestId: string) =>
    apiRequest<CeremonyTable>(
      `/admin/tables/${encodeURIComponent(tableId)}/guests/${encodeURIComponent(guestId)}`,
      { method: 'PUT' },
    ),
  unseatGuest: (tableId: string, guestId: string) =>
    apiRequest<CeremonyTable>(
      `/admin/tables/${encodeURIComponent(tableId)}/guests/${encodeURIComponent(guestId)}`,
      { method: 'DELETE' },
    ),

  dressCodes: () => apiRequest<PageData<DressCode> | DressCode[]>('/admin/dress-codes'),
  createDressCode: (payload: Record<string, unknown>) =>
    apiRequest<DressCode>('/admin/dress-codes', { method: 'POST', body: payload as never }),
  updateDressCode: (id: string, payload: Record<string, unknown>) =>
    apiRequest<DressCode>(`/admin/dress-codes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload as never,
    }),
  deleteDressCode: (id: string) =>
    apiRequest<void>(`/admin/dress-codes/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  uploadDressCodeImage: (id: string, file: File) => {
    const form = new FormData();
    form.set('file', file);
    return apiRequest<DressCode>(`/admin/dress-codes/${encodeURIComponent(id)}/image`, {
      method: 'POST',
      body: form,
    });
  },
  removeDressCodeImage: (id: string) =>
    apiRequest<DressCode>(`/admin/dress-codes/${encodeURIComponent(id)}/image`, {
      method: 'DELETE',
    }),
  linkDressCode: (id: string, ceremonyId: string) =>
    apiRequest<DressCode>(
      `/admin/dress-codes/${encodeURIComponent(id)}/ceremonies/${encodeURIComponent(ceremonyId)}`,
      { method: 'PUT' },
    ),
  unlinkDressCode: (id: string, ceremonyId: string) =>
    apiRequest<DressCode>(
      `/admin/dress-codes/${encodeURIComponent(id)}/ceremonies/${encodeURIComponent(ceremonyId)}`,
      { method: 'DELETE' },
    ),

  guests: (params: Record<string, string | number | undefined>) =>
    apiRequest<PageData<GuestRecord> | GuestRecord[]>(`/admin/guests${query(params)}`),
  tableGuests: async (): Promise<GuestRecord[]> => {
    const guests: GuestRecord[] = [];
    let cursor: string | null = '1';
    while (cursor) {
      const result: PageData<GuestRecord> | GuestRecord[] = await apiRequest(
        `/admin/guests${query({ limit: 100, page: cursor })}`,
      );
      guests.push(...unwrapItems(result));
      cursor = Array.isArray(result) ? null : (result.nextCursor ?? null);
    }
    return guests;
  },
  guest: (id: string) => apiRequest<GuestRecord>(`/admin/guests/${encodeURIComponent(id)}`),
  guestPreview: (id: string) =>
    apiRequest<AdminGuestPreview>(`/admin/preview/guests/${encodeURIComponent(id)}`),
  guestPreviewPdfUrl: (guestId: string, assignmentId: string) =>
    `${API_BASE}/admin/preview/guests/${encodeURIComponent(guestId)}?format=pdf&assignmentId=${encodeURIComponent(assignmentId)}`,
  createGuest: (payload: Record<string, unknown>) =>
    apiRequest<GuestRecord>('/admin/guests', { method: 'POST', body: payload as never }),
  updateGuest: (id: string, payload: Record<string, unknown>) =>
    apiRequest<GuestRecord>(`/admin/guests/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload as never,
    }),
  uploadGuestInvitationFile: (id: string, file: File) => {
    const form = new FormData();
    form.set('file', file);
    return apiRequest<GuestRecord>(`/admin/guests/${encodeURIComponent(id)}/invitation-file`, {
      method: 'POST',
      body: form,
    });
  },
  removeGuestInvitationFile: (id: string) =>
    apiRequest<GuestRecord>(`/admin/guests/${encodeURIComponent(id)}/invitation-file`, {
      method: 'DELETE',
    }),
  guestAction: (id: string, action: 'suspend' | 'reactivate' | 'archive', reason: string) =>
    apiRequest<GuestRecord>(`/admin/guests/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      body: { reason } as never,
    }),
  deleteGuest: (id: string, reason: string) =>
    apiRequest<void>(`/admin/guests/${encodeURIComponent(id)}/delete`, {
      method: 'POST',
      body: { reason } as never,
    }),
  changeGuestPhone: (
    id: string,
    payload: { countryCode: string; phone: string; reason: string; version: number },
  ) =>
    apiRequest<GuestRecord>(`/admin/guests/${encodeURIComponent(id)}/phone-change`, {
      method: 'POST',
      body: payload as never,
    }),
  setAssignment: (guestId: string, ceremonyId: string, payload: Record<string, unknown>) =>
    apiRequest<GuestRecord['assignments']>(
      `/admin/guests/${encodeURIComponent(guestId)}/assignments/${encodeURIComponent(ceremonyId)}`,
      { method: 'PUT', body: payload as never },
    ),

  agents: () => apiRequest<PageData<AgentRecord> | AgentRecord[]>('/admin/agents'),
  createAgent: (payload: Record<string, unknown>) =>
    apiRequest<AgentRecord>('/admin/agents', { method: 'POST', body: payload as never }),
  updateAgent: (id: string, payload: Record<string, unknown>) =>
    apiRequest<AgentRecord>(`/admin/agents/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload as never,
    }),
  deleteAgent: (id: string, reason: string) =>
    apiRequest<{ archived: boolean }>(`/admin/agents/${encodeURIComponent(id)}/delete`, {
      method: 'POST',
      body: { reason } as never,
    }),

  admissions: (params: Record<string, string | undefined>) =>
    apiRequest<PageData<Admission> | Admission[]>(`/admin/admissions${query(params)}`),
  voidAdmission: (id: string, reason: string, version?: number) =>
    apiRequest<Admission>(`/admin/admissions/${encodeURIComponent(id)}/void`, {
      method: 'POST',
      body: { reason, version } as never,
    }),
  replaceAdmission: (id: string, reason: string, admittedCount: number, version?: number) =>
    apiRequest<Admission>(`/admin/admissions/${encodeURIComponent(id)}/replace`, {
      method: 'POST',
      body: { reason, admittedCount, version } as never,
    }),
  manualAdmission: (payload: Record<string, unknown>, idempotencyKey: string) =>
    apiRequest<Admission>('/admin/admissions/manual', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: payload as never,
    }),
  audit: (params: Record<string, string | undefined>) =>
    apiRequest<PageData<AuditEvent> | AuditEvent[]>(`/admin/audit${query(params)}`),

  checkinCeremonies: () =>
    apiRequest<PageData<CheckinCeremony> | CheckinCeremony[]>('/checkin/ceremonies'),
  checkinGuests: (ceremonyId: string, search: string) =>
    apiRequest<PageData<CheckinGuestMatch> | CheckinGuestMatch[]>(
      `/checkin/ceremonies/${encodeURIComponent(ceremonyId)}/guests${query({ q: search })}`,
    ),
  verifyGuest: (ceremonyId: string, guestId: string) =>
    apiRequest<CheckinVerification>(
      `/checkin/ceremonies/${encodeURIComponent(ceremonyId)}/verify`,
      { method: 'POST', body: { guestId } as never },
    ),
  confirmAdmission: (
    ceremonyId: string,
    verificationId: string,
    admittedCount: number,
    idempotencyKey: string,
  ) =>
    apiRequest<{ admission: Admission } | Admission>(
      `/checkin/ceremonies/${encodeURIComponent(ceremonyId)}/admissions`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: { verificationId, admittedCount } as never,
      },
    ),
  checkinStats: (ceremonyId: string) =>
    apiRequest<CheckinStats>(`/checkin/ceremonies/${encodeURIComponent(ceremonyId)}/stats`),
};

export function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Une erreur inattendue s'est produite.";
}

export function fieldError(error: unknown, field: string) {
  if (!(error instanceof ApiError)) return undefined;
  return error.fieldErrors.find((item) => item.field === field || item.path === field)?.message;
}

export function openDownloadedBlob(blob: Blob, filename?: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.target = '_blank';
  anchor.rel = 'noopener';
  if (filename) anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
